import { MEMORY_TRAIN_KINDS } from './memory'
import type { BuiltInTool, BuiltInToolError } from './types'

/**
 * Upload one note to a team or the whole enterprise, with no task in the call. `POST /v1/memory/train` under
 * `memory:write`, for a person's own agent over the API and the OS MCP server.
 *
 * This is not `memory.train`. That tool is a run's, at the task layer by default, and its schema is too open for
 * this server. This name stays out of `MEMORY_TOOL_NAMES`, so the run dispatcher does not gain a case. A task that
 * declares it is refused when it is saved (`tool_not_run_callable`). Task, member and role notes stay on the task
 * route.
 */

const MEMORY_ORG_LAYER_REQUIRED: BuiltInToolError = {
    code: 'memory_org_layer_required',
    when: '`layer` is not team or enterprise. Task, member and role notes are not uploaded here.',
    handling: 'tool_error',
}

const MEMORY_TEAM_REQUIRED: BuiltInToolError = {
    code: 'memory_team_required',
    when: '`layer` is team and `teamId` is missing.',
    handling: 'tool_error',
}

const MEMORY_TEAM_NOT_APPLICABLE: BuiltInToolError = {
    code: 'memory_team_not_applicable',
    when: '`layer` is enterprise and `teamId` is set.',
    handling: 'tool_error',
}

const TEAM_NOT_FOUND: BuiltInToolError = {
    code: 'team_not_found',
    when: '`teamId` names no team in this enterprise.',
    handling: 'tool_error',
}

const MEMORY_SOURCE_INVALID: BuiltInToolError = {
    code: 'memory_source_invalid',
    when: '`source` is not an object, or its `eventId` or `revision` is there but is not a string. Nothing is stored.',
    handling: 'tool_error',
}

const RESERVED_SOURCE_EVENT: BuiltInToolError = {
    code: 'reserved_source_event',
    when: '`source.eventId` is a key the OS writes itself, such as team:<teamId>:… or a gate-learning key.',
    handling: 'tool_error',
}

export const MEMORY_UPLOAD_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'memory.upload',
        family: 'memory',
        summary: 'Upload one note to a team or the whole enterprise.',
        description:
            'Upload one note to a team or the whole enterprise. Pass layer (team or enterprise) and body. A team ' +
            'upload also passes teamId, a team in this enterprise; an enterprise upload passes no teamId. Optionally ' +
            'pass kind (fact, the default; inference, preference or correction) and source ({ eventId, revision }), ' +
            'the event the note was learned from, so uploading it again is not stored twice. To correct a note, pass ' +
            'kind correction and supersedes ({ entryId }) naming the entry it replaces. Author and enterprise come ' +
            'from the caller. Task, member and role notes are not uploaded here. A task that declares this tool is ' +
            'refused when it is saved. eventId and revision are strings. A source that is not an object, or a half ' +
            'that is not a string, is refused with memory_source_invalid. A call that leaves source out, or leaves ' +
            'either half empty or out, is stored as a new note each time.',
        inputSchema: {
            type: 'object',
            properties: {
                layer: {
                    type: 'string',
                    enum: ['team', 'enterprise'],
                    description: 'team or enterprise. A team upload names teamId.',
                },
                teamId: {
                    type: 'string',
                    description: 'The team, when layer is team. Leave it out when layer is enterprise.',
                },
                body: { type: 'string', description: 'The note to retain, one claim in a sentence.' },
                kind: {
                    type: 'string',
                    enum: [...MEMORY_TRAIN_KINDS],
                    description:
                        'fact, the default; inference, preference or correction. A correction replaces the note named in supersedes.',
                },
                source: {
                    type: 'object',
                    description:
                        'Where the note was learned, as { eventId, revision }, both strings. The same pair in this enterprise is stored once.',
                },
                supersedes: {
                    type: 'object',
                    description:
                        'The entry this note corrects, as { entryId }. With kind correction it retires that entry.',
                },
            },
            required: ['layer', 'body'],
            additionalProperties: false,
        },
        returns:
            '`{ status, entry, usage }`. `status` is `accepted`, `retained`, `consolidating` or `failed`. Author and enterprise come from the caller.',
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote:
            'Keyed on source (eventId, revision). A call that leaves source out, or leaves either half empty or out, has no key, so each such call is stored as its own note.',
        errors: [
            MEMORY_ORG_LAYER_REQUIRED,
            MEMORY_TEAM_REQUIRED,
            MEMORY_TEAM_NOT_APPLICABLE,
            TEAM_NOT_FOUND,
            MEMORY_SOURCE_INVALID,
            RESERVED_SOURCE_EVENT,
        ],
        api: { method: 'POST', path: '/v1/memory/train', scope: 'memory:write' },
        examples: [
            {
                input: {
                    layer: 'team',
                    teamId: '5f0c1d2e-0000-4000-8000-000000000012',
                    body: 'Releases ship on Tuesday.',
                    kind: 'fact',
                    source: { eventId: 'note-1', revision: '1' },
                },
            },
        ],
        public: true,
    },
]
